/**
 * "How disputes resolve" — the read-only drawer on `/support/disputes` (admin doc 18 §2).
 *
 * The founder's decision tree (product doc 13 §4.1, branches a–f) beside the outcome matrix the
 * server enforces. The matrix and the ceilings are read from `/v1/admin/disputes/outcome-matrix`,
 * which is served off the same sets the resolve call checks, so this panel cannot describe a rule
 * the server does not hold. Branch (c)'s decline is macro M9, shown as the row support edits.
 */
import { Note } from '../components/primitives';
import { DataTable } from '../components/data';
import { ErrorState, LoadingState, Modal } from '../components/feedback';
import { Section } from '../components/kit';
import { errorMessage } from '../lib/api/client';
import { formatInr } from '../lib/money';
import { useOutcomeMatrix, useSupportMacros } from '../lib/api/hooks/support';

/** Product doc 13 §4.1, one line per branch: what is checked, and where it lands. */
export const BRANCHES: readonly { key: string; claim: string; resolution: string }[] = [
  {
    key: 'a',
    claim: 'Pandit never came',
    resolution:
      'Verify check-in, start OTP and contact. Confirmed: full refund including the platform fee, a goodwill credit, a backup or reschedule at the devotee’s option, zero payout and the enforcement ladder.',
  },
  {
    key: 'b',
    claim: 'Late, but the puja happened',
    resolution:
      'Check-in against the scheduled start. Beyond grace but completed with OTPs: goodwill only; the payout is not docked unless the lateness broke a documented SLA (a missed muhurat is treated as c).',
  },
  {
    key: 'c',
    claim: 'Quality not as promised',
    resolution:
      'Evidence review. An item not delivered: partial refund of that line from the booking’s price version. A material failure: full or majority refund (L2/L3). Subjective dissatisfaction with an as-described service: no refund by default; L1 may offer goodwill; decline with M9.',
  },
  {
    key: 'd',
    claim: 'Devotee cancellation',
    resolution: 'The cancellation bands apply, mechanically.',
  },
  {
    key: 'e',
    claim: 'Samagri missing or wrong',
    resolution:
      'Item-level refund of the samagri line from the pinned price version, recovered from whoever supplied it. Devotee-provided samagri short: no refund, noted on the booking.',
  },
  {
    key: 'f',
    claim: 'OTP entered, completion disputed',
    resolution:
      'Senior review, L2 at least: who entered the OTP, when and from where, check-in and photos, a call to both sides. Substantiated coercion or a mis-entry is treated as a or c.',
  },
];

const RULE: Record<string, string> = {
  required: 'must name one',
  optional: 'may',
  forbidden: 'never',
};

/** Mounted only while open, so neither read runs for an operator who never asks. */
export function HowDisputesResolve({ onClose }: { onClose: () => void }) {
  const matrix = useOutcomeMatrix();
  const macros = useSupportMacros();
  const m9 = macros.data?.items.find((m) => m.code === 'M9' && m.locale === 'en');

  return (
    <Modal title="How disputes resolve" width={880} onClose={onClose}>
      <Section title="The decision tree" note="Product doc 13 §4.1">
        {BRANCHES.map((b) => (
          <p key={b.key} style={{ margin: '0 0 var(--sp-sm)' }}>
            <strong>
              ({b.key}) {b.claim}
            </strong>{' '}
            — {b.resolution}
          </p>
        ))}
      </Section>

      <Section title="What each outcome carries" note="Enforced on every resolve">
        {matrix.isPending && <LoadingState message="Reading the matrix…" />}
        {matrix.isError && (
          <ErrorState title="Could not read the matrix" cause={errorMessage(matrix.error)} />
        )}
        {matrix.isSuccess && (
          <>
            <DataTable
              density="compact"
              columns={[
                { key: 'outcome', label: 'Outcome' },
                { key: 'refund', label: 'Refund to the devotee' },
                { key: 'forfeit', label: 'Taken from the pandit' },
              ]}
              rows={matrix.data.outcomes.map((o) => ({
                id: o.outcome,
                outcome: o.outcome.replace(/_/g, ' '),
                refund: RULE[o.refund],
                forfeit: RULE[o.forfeit],
              }))}
            />
            <Note>
              L1 (support) may give goodwill up to {formatInr(matrix.data.l1_goodwill_max_paise)} and
              refund samagri lines up to {formatInr(matrix.data.l1_samagri_refund_max_paise)}. L2
              (finance) gives full refunds on branches a–e, payout reductions and credits up to{' '}
              {formatInr(matrix.data.l2_credit_max_paise)}. Anything above, every clawback and every
              adverse branch-f finding is L3. Above the four-eyes threshold a second admin agrees.
            </Note>
            <Note>
              The standard credits: {formatInr(matrix.data.no_show_goodwill_paise)} on a confirmed
              no-show, on top of the full refund, and {formatInr(matrix.data.late_arrival_goodwill_paise)}{' '}
              when the pandit checked in more than {matrix.data.late_arrival_goodwill_after_minutes} minutes
              late but the puja was completed.
            </Note>
          </>
        )}
      </Section>

      <Section title="Declining an outcome expectation" note="Macro M9, edited under Reply macros">
        <Note>
          We make no outcome claims, so we never refund because a ritual did not bring a hoped-for
          result.
        </Note>
        {m9 ? (
          <>
            <strong>{m9.title}</strong>
            <p style={{ whiteSpace: 'pre-wrap' }}>{m9.body}</p>
          </>
        ) : (
          <p>M9 could not be read; open Support → Reply macros.</p>
        )}
      </Section>
    </Modal>
  );
}
